<template>
  <v-dialog
    v-model="isOpen"
    :max-width="width"
    persistent
    transition="fade-transition"
    @click:outside="handleClose"
  >
    <v-card class="quote-requested-modal">
      <!-- Header -->
      <div class="modal-header">
        <h3 class="header-title">Your order is on progress</h3>
        <button class="close-button" @click="handleClose" aria-label="Close">
          <XIcon />
        </button>
      </div>

      <!-- Content -->
      <div class="modal-content">
        <!-- Illustration -->
        <div class="illustration-container">
          <img
            src="/images/maker-cart/ok-bro.svg"
            alt="Order processed illustration"
            class="illustration"
          />
        </div>

        <!-- Title and Description -->
        <div class="text-content">
          <h2 class="main-title">Order Processed!</h2>
          <div class="description">
            <p>
              Thanks for reaching out - we’ll get back with a quote in 24 hours
            </p>
          </div>
        </div>
      </div>
    </v-card>
  </v-dialog>
</template>

<script setup>
import { defineProps, defineEmits, computed } from "vue";
import { XIcon } from "@/components/icons";

const props = defineProps({
  modelValue: {
    type: Boolean,
    default: false,
  },
  loading: {
    type: Boolean,
    default: false,
  },
  width: {
    type: [String, Number],
    default: 812,
  },
});

const emit = defineEmits(["update:modelValue", "close"]);

const isOpen = computed({
  get: () => props.modelValue,
  set: (value) => emit("update:modelValue", value),
});

const handleClose = () => {
  emit("close");
  isOpen.value = false;
};
</script>

<style scoped>
.quote-requested-modal {
  background: white;
  border: 2px solid #f2f2f2;
  border-radius: 16px;
  padding: 50px;
  display: flex;
  flex-direction: column;
  gap: 24px;
  width: 100%;
  box-shadow:
    0px 1px 2px 2px rgba(33, 51, 96, 0.02),
    0px 4px 8px 2px rgba(55, 57, 108, 0.08);
}

.modal-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  padding-bottom: 11.504px;
  border-bottom: 1px solid #f2f2f2;
}

.header-title {
  font-family: "League Spartan", sans-serif;
  font-weight: 700;
  font-size: var(--fs-large);
  line-height: 28px;
  color: var(--primary-blue);
  margin: 0;
  letter-spacing: 0;
}

.close-button {
  background: none;
  border: none;
  cursor: pointer;
  padding: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  color: var(--primary-blue);
  transition: opacity 0.2s ease;
}

.close-button:hover {
  opacity: 0.7;
}

.close-button:active {
  opacity: 0.5;
}

.modal-content {
  display: flex;
  flex-direction: column;
  gap: 24px;
  align-items: center;
  width: 100%;
}

.illustration-container {
  width: 500px;
  height: 500px;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
}

.illustration {
  width: 100%;
  height: 100%;
  object-fit: contain;
}

.text-content {
  display: flex;
  flex-direction: column;
  gap: 8px;
  align-items: center;
  text-align: center;
  width: 100%;
}

.main-title {
  font-family: "League Spartan", sans-serif;
  font-weight: 700;
  font-size: 32px;
  line-height: 40px;
  color: #e00069;
  margin: 0;
  letter-spacing: 0;
}

.description {
  font-family: "League Spartan", sans-serif;
  font-weight: 400;
  font-size: var(--fs-medium);
  line-height: 24px;
  color: #3b4648;
  letter-spacing: 0.08px;
  max-width: 712px;
}

.description p {
  margin: 0;
}

.description p + p {
  margin-top: 0;
}

.email-link {
  color: #e00069;
  text-decoration: none;
  transition: opacity 0.2s ease;
}

.email-link:hover {
  opacity: 0.8;
  text-decoration: underline;
}

.modal-actions {
  display: flex;
  gap: 24px;
  width: 100%;
  justify-content: center;
}

.action-button {
  flex: 1;
  min-width: 0;
  height: 48px;
}

/* Fade transition animations */
.fade-enter-active,
.fade-leave-active {
  transition: all 0.3s cubic-bezier(0.25, 0.8, 0.25, 1);
}

.fade-enter-from {
  opacity: 0;
  transform: scale(0.95);
}

.fade-leave-to {
  opacity: 0;
  transform: scale(0.95);
}

.fade-enter-to,
.fade-leave-from {
  opacity: 1;
  transform: scale(1);
}

:deep(.v-dialog) {
  transition: all 0.3s cubic-bezier(0.25, 0.8, 0.25, 1);
}

:deep(.v-overlay__content) {
  transition: all 0.3s cubic-bezier(0.25, 0.8, 0.25, 1);
}

.quote-requested-modal {
  will-change: transform, opacity;
  backface-visibility: hidden;
  transform: translateZ(0);
  position: relative;
}

/* Responsive Design */
@media (max-width: 900px) {
  .quote-requested-modal {
    padding: 40px 30px;
  }

  .illustration-container {
    width: 100%;
    max-width: 400px;
    height: auto;
    aspect-ratio: 1;
  }
}

@media (max-width: 768px) {
  .quote-requested-modal {
    padding: 30px 20px;
    gap: 20px;
  }

  .header-title {
    font-size: 18px;
    line-height: 24px;
  }

  .main-title {
    font-size: var(--fs-xl);
    line-height: 32px;
  }

  .illustration-container {
    max-width: 300px;
  }

  .description {
    font-size: var(--fs-small);
    line-height: 20px;
  }

  .modal-actions {
    flex-direction: column;
    gap: 16px;
  }

  .action-button {
    width: 100%;
  }
}
</style>
